/**
 * Content Rule Center Actions
 * Actions for fetching and managing Content Rule Center data
 */
import { loadPromptFieldOptions } from '../../../services/Content';
import {
  fetchContentRulesCategories,
  fetchSeverityWiseSummary,
  fetchCategoryWiseSummary,
  fetchContentRules,
} from '../../../services/ContentRules';
import Logger from '../../../utils/logger';
import store from '../../markettingContentGeneration';
import {
  contentRuleCenterStateSegment,
  ContentRuleCenterSelectedFilters,
} from '../stateSegments/contentRuleCenterStateSegment';
import { FormFields } from '../../../types/PromptForm';
import { CannedParams } from '../../../types/Content';

/**
 * Phase 1: Fetches dropdown options and severity summary (quality cards)
 * Called when modal opens - shows full page loader until complete
 */
export const fetchInitialData = async (): Promise<void> => {
  try {
    store.set(contentRuleCenterStateSegment, prev => ({
      ...prev,
      isInitialLoading: true,
      error: null,
    }));

    // Fetch dropdown options and severity summary in parallel
    const [
      countriesResponse,
      brandsResponse,
      audiencesResponse,
      channelsResponse,
      categoriesResponse,
      severityResponse,
    ] = await Promise.all([
      loadPromptFieldOptions(FormFields.COUNTRY, { fetchAll: true }),
      loadPromptFieldOptions(FormFields.BRAND, { fetchAll: true }),
      loadPromptFieldOptions(FormFields.AUDIENCE, { fetchAll: true }),
      loadPromptFieldOptions(FormFields.CHANNEL, { fetchAll: true }),
      fetchContentRulesCategories(),
      fetchSeverityWiseSummary(),
    ]);

    // Extract options from responses
    const countriesData = countriesResponse.data as
      | { items?: CannedParams[] }
      | undefined;
    const brandsData = brandsResponse.data as
      | { items?: CannedParams[] }
      | undefined;
    const audiencesData = audiencesResponse.data as
      | { items?: CannedParams[] }
      | undefined;
    const channelsData = channelsResponse.data as
      | { items?: CannedParams[] }
      | undefined;

    const countriesItems: CannedParams[] = countriesData?.items || [];
    const brandsItems: CannedParams[] = brandsData?.items || [];
    const audiencesItems: CannedParams[] = audiencesData?.items || [];
    const channelsItems: CannedParams[] = channelsData?.items || [];
    const categoriesData = categoriesResponse.success
      ? categoriesResponse.data.items
      : [];

    // Update state with fetched options and severity summary
    store.set(contentRuleCenterStateSegment, prev => ({
      ...prev,
      filterOptions: {
        countriesOptions: {
          loading: false,
          options: countriesItems,
          isFetched: true,
          isSuccess: countriesResponse.success,
        },
        brandsOptions: {
          loading: false,
          options: brandsItems,
          isFetched: true,
          isSuccess: brandsResponse.success,
        },
        audiencesOptions: {
          loading: false,
          options: audiencesItems,
          isFetched: true,
          isSuccess: audiencesResponse.success,
        },
        channelsOptions: {
          loading: false,
          options: channelsItems,
          isFetched: true,
          isSuccess: channelsResponse.success,
        },
        categoriesOptions: {
          loading: false,
          options: categoriesData,
          isFetched: true,
          isSuccess: categoriesResponse.success,
        },
      },
      selectedFilters: {
        countries: countriesItems.map(item => item.name || item.id),
        brands: brandsItems.map(item => item.name || item.id),
        audiences: audiencesItems.map(item => item.name || item.id),
        channels: channelsItems.map(item => item.name || item.id),
        categories: categoriesData.map(cat => cat.name),
      },
      severitySummary: {
        loading: false,
        isFetched: true,
        totalRuleCount: severityResponse.success
          ? severityResponse.data.totalRuleCount
          : '0',
        severityRuleCounts: severityResponse.success
          ? severityResponse.data.severityRuleCounts
          : [],
        error: severityResponse.success ? null : 'Failed to fetch severity',
      },
      isInitialLoading: false,
      areFiltersLoaded: true,
    }));
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch initial data';
    Logger.error(new Error(errorMessage));

    store.set(contentRuleCenterStateSegment, prev => ({
      ...prev,
      isInitialLoading: false,
      error: errorMessage,
    }));
  }
};

/**
 * Phase 2: Fetches category summaries (accordion headers)
 * Called after initial data is loaded - shows loader in table area
 */
export const fetchCategorySummaries = async (): Promise<void> => {
  try {
    store.set(contentRuleCenterStateSegment, prev => ({
      ...prev,
      isTableLoading: true,
      categorySummary: {
        ...prev.categorySummary,
        loading: true,
        error: null,
      },
    }));

    const currentState = store.get(contentRuleCenterStateSegment);
    const currentFilters = currentState.selectedFilters;
    const filterOptions = currentState.filterOptions;

    // Only pass filters if not all items are selected
    const params: {
      countries?: string;
      brands?: string;
      audience?: string;
      channels?: string;
      categories?: string;
    } = {};

    // Check if all countries are selected
    const allCountries = filterOptions.countriesOptions.options.map(
      item => item.name || item.id
    );
    if (
      currentFilters.countries.length > 0 &&
      currentFilters.countries.length < allCountries.length
    ) {
      params.countries = currentFilters.countries.join(',');
    }

    // Check if all brands are selected
    const allBrands = filterOptions.brandsOptions.options.map(
      item => item.name || item.id
    );
    if (
      currentFilters.brands.length > 0 &&
      currentFilters.brands.length < allBrands.length
    ) {
      params.brands = currentFilters.brands.join(',');
    }

    // Check if all audiences are selected
    const allAudiences = filterOptions.audiencesOptions.options.map(
      item => item.name || item.id
    );
    if (
      currentFilters.audiences.length > 0 &&
      currentFilters.audiences.length < allAudiences.length
    ) {
      params.audience = currentFilters.audiences.join(',');
    }

    // Check if all channels are selected
    const allChannels = filterOptions.channelsOptions.options.map(
      item => item.name || item.id
    );
    if (
      currentFilters.channels.length > 0 &&
      currentFilters.channels.length < allChannels.length
    ) {
      params.channels = currentFilters.channels.join(',');
    }

    // Check if all categories are selected
    const allCategories = filterOptions.categoriesOptions.options.map(
      cat => cat.name
    );
    if (
      currentFilters.categories.length > 0 &&
      currentFilters.categories.length < allCategories.length
    ) {
      params.categories = currentFilters.categories.join(',');
    }

    const response = await fetchCategoryWiseSummary(params);

    if (response.success && response.data) {
      store.set(contentRuleCenterStateSegment, prev => ({
        ...prev,
        isTableLoading: false,
        categorySummary: {
          loading: false,
          isFetched: true,
          categoryRuleCounts: response.data.categoryRuleCounts,
          error: null,
        },
      }));
    } else {
      throw new Error(response.message || 'Failed to fetch category summary');
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to fetch category summary';
    Logger.error(new Error(errorMessage));

    store.set(contentRuleCenterStateSegment, prev => ({
      ...prev,
      isTableLoading: false,
      categorySummary: {
        ...prev.categorySummary,
        loading: false,
        isFetched: true,
        error: errorMessage,
      },
    }));
  }
};

/**
 * Phase 3: Fetches rules for a specific category when accordion is opened
 * Supports pagination for infinite scroll
 */
export const fetchCategoryRules = async (
  categoryId: string,
  categoryName: string,
  page: number = 1,
  pageSize: number = 20,
  append: boolean = false
): Promise<void> => {
  try {
    // Capture current rules before setting loading state to prevent data loss
    const stateBeforeLoading = store.get(contentRuleCenterStateSegment);
    const currentRules =
      stateBeforeLoading.categoryRules[categoryId]?.rules || [];

    // Set loading state for this category
    store.set(contentRuleCenterStateSegment, prev => {
      const existingCategoryState = prev.categoryRules[categoryId];
      return {
        ...prev,
        categoryRules: {
          ...prev.categoryRules,
          [categoryId]: {
            loading: true,
            isFetched: existingCategoryState?.isFetched || false,
            rules: existingCategoryState?.rules || [],
            pagination: existingCategoryState?.pagination || {
              page: 1,
              pageSize: 20,
              totalPages: 0,
              total: 0,
              nextPage: null,
              previousPage: null,
            },
            error: null,
          },
        },
      };
    });

    const currentState = store.get(contentRuleCenterStateSegment);
    const currentFilters = currentState.selectedFilters;
    const filterOptions = currentState.filterOptions;

    const params: {
      countries?: string;
      brands?: string;
      audience?: string;
      channels?: string;
      categories: string;
      page: number;
      pageSize: number;
    } = {
      categories: categoryName,
      page,
      pageSize,
    };

    // Only pass filters if not all items are selected
    const allCountries = filterOptions.countriesOptions.options.map(
      item => item.name || item.id
    );
    if (
      currentFilters.countries.length > 0 &&
      currentFilters.countries.length < allCountries.length
    ) {
      params.countries = currentFilters.countries.join(',');
    }

    const allBrands = filterOptions.brandsOptions.options.map(
      item => item.name || item.id
    );
    if (
      currentFilters.brands.length > 0 &&
      currentFilters.brands.length < allBrands.length
    ) {
      params.brands = currentFilters.brands.join(',');
    }

    const allAudiences = filterOptions.audiencesOptions.options.map(
      item => item.name || item.id
    );
    if (
      currentFilters.audiences.length > 0 &&
      currentFilters.audiences.length < allAudiences.length
    ) {
      params.audience = currentFilters.audiences.join(',');
    }

    const allChannels = filterOptions.channelsOptions.options.map(
      item => item.name || item.id
    );
    if (
      currentFilters.channels.length > 0 &&
      currentFilters.channels.length < allChannels.length
    ) {
      params.channels = currentFilters.channels.join(',');
    }

    const response = await fetchContentRules(params);

    if (response.success && response.data) {
      store.set(contentRuleCenterStateSegment, prev => {
        // Use the captured rules when appending, not prev which might be stale
        const existingRules = append ? currentRules : [];
        return {
          ...prev,
          categoryRules: {
            ...prev.categoryRules,
            [categoryId]: {
              loading: false,
              isFetched: true,
              rules: [...existingRules, ...response.data.rules],
              pagination: response.data.pagination,
              error: null,
            },
          },
        };
      });
    } else {
      throw new Error('Failed to fetch category rules');
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch category rules';
    Logger.error(new Error(errorMessage));

    store.set(contentRuleCenterStateSegment, prev => ({
      ...prev,
      categoryRules: {
        ...prev.categoryRules,
        [categoryId]: {
          ...prev.categoryRules[categoryId],
          loading: false,
          isFetched: true,
          error: errorMessage,
        },
      },
    }));
  }
};

/**
 * Updates selected filter values
 */
export const updateContentRuleCenterFilters = (
  filterType: keyof ContentRuleCenterSelectedFilters,
  values: string[]
): void => {
  store.set(contentRuleCenterStateSegment, prev => ({
    ...prev,
    selectedFilters: {
      ...prev.selectedFilters,
      [filterType]: values,
    },
    // Clear all cached category rules since filters changed
    // This forces refetch when accordion reopens with new filter criteria
    categoryRules: {},
  }));
};

/**
 * Updates multiple filter values at once (batch update)
 * This prevents multiple API calls when updating several filters simultaneously
 */
export const updateContentRuleCenterFiltersBatch = async (
  filters: Partial<ContentRuleCenterSelectedFilters>
): Promise<void> => {
  store.set(contentRuleCenterStateSegment, prev => ({
    ...prev,
    selectedFilters: {
      ...prev.selectedFilters,
      ...filters,
    },
    // Clear all cached category rules since filters changed
    // This forces refetch when accordion reopens with new filter criteria
    categoryRules: {},
  }));

  // Fetch category summaries only once after all filters are updated
  await fetchCategorySummaries();
};

/**
 * Resets all selected filters to empty arrays
 */
export const resetContentRuleCenterFilters = (): void => {
  store.set(contentRuleCenterStateSegment, prev => ({
    ...prev,
    selectedFilters: {
      countries: [],
      brands: [],
      audiences: [],
      channels: [],
      categories: [],
    },
    // Clear all cached category rules since filters were reset
    // This forces refetch when accordion reopens with no filter criteria
    categoryRules: {},
  }));
};

/**
 * Initializes Content Rule Center by running Phase 1 and Phase 2 in sequence
 * This should be called when the modal opens
 */
export const initializeContentRuleCenter = async (): Promise<boolean> => {
  try {
    // Phase 1: Fetch dropdowns and quality cards
    await fetchInitialData();

    // Phase 2: Fetch category summaries
    await fetchCategorySummaries();

    store.set(contentRuleCenterStateSegment, prev => ({
      ...prev,
      isInitialized: true,
    }));

    return true;
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to initialize Content Rule Center';
    Logger.error(new Error(errorMessage));

    store.set(contentRuleCenterStateSegment, prev => ({
      ...prev,
      isInitialLoading: false,
      isInitialized: false,
      error: errorMessage,
    }));

    return false;
  }
};

/**
 * Refreshes Content Rule Center data (useful for retry scenarios)
 */
export const refreshContentRuleCenter = async (): Promise<boolean> => {
  return initializeContentRuleCenter();
};

/**
 * Resets Content Rule Center state to initial values
 */
export const resetContentRuleCenter = (): void => {
  store.set(contentRuleCenterStateSegment, {
    filterOptions: {
      countriesOptions: { loading: false, options: [], isFetched: false },
      brandsOptions: { loading: false, options: [], isFetched: false },
      audiencesOptions: { loading: false, options: [], isFetched: false },
      channelsOptions: { loading: false, options: [], isFetched: false },
      categoriesOptions: { loading: false, options: [], isFetched: false },
    },
    selectedFilters: {
      countries: [],
      brands: [],
      audiences: [],
      channels: [],
      categories: [],
    },
    severitySummary: {
      loading: false,
      isFetched: false,
      totalRuleCount: '0',
      severityRuleCounts: [],
      error: null,
    },
    categorySummary: {
      loading: false,
      isFetched: false,
      categoryRuleCounts: [],
      error: null,
    },
    contentRules: {
      loading: false,
      isFetched: false,
      rules: [],
      pagination: {
        page: 1,
        pageSize: 20,
        totalPages: 0,
        total: 0,
        nextPage: null,
        previousPage: null,
      },
      error: null,
    },
    categoryRules: {},
    isInitialLoading: false,
    isTableLoading: false,
    isInitialized: false,
    areFiltersLoaded: false,
    error: null,
  });
};
